/**
 * AGC: Conditional Logger Utility
 * AGC: Only logs in development mode to reduce console noise in production
 * AGC: Supports log levels: debug, info, warn, error
 */

// AGC: Check if we're in development mode
const isDevelopment = import.meta.env.DEV || import.meta.env.MODE === 'development'

// AGC: Log level configuration (can be adjusted via env var)
const LOG_LEVEL = (import.meta.env.VITE_LOG_LEVEL || 'info').toLowerCase() as 'debug' | 'info' | 'warn' | 'error'

// AGC: Log level priority (higher = more important)
const LOG_LEVELS = {
  debug: 0,
  info: 1,
  warn: 2,
  error: 3,
} as const

/**
 * AGC: Check if a log level should be displayed
 */
function shouldLog(level: keyof typeof LOG_LEVELS): boolean {
  if (!isDevelopment) {
    // AGC: In production, only show errors and warnings
    return level === 'error' || level === 'warn'
  }
  
  // AGC: In development, respect LOG_LEVEL setting
  return LOG_LEVELS[level] >= LOG_LEVELS[LOG_LEVEL]
}

/**
 * AGC: Debug logger (only in development, respects LOG_LEVEL)
 */
export const logger = {
  debug: (...args: any[]) => {
    if (shouldLog('debug')) {
      console.log('[DEBUG]', ...args)
    }
  },
  
  info: (...args: any[]) => {
    if (shouldLog('info')) {
      console.log('[INFO]', ...args)
    }
  },
  
  warn: (...args: any[]) => {
    if (shouldLog('warn')) {
      console.warn('[WARN]', ...args)
    }
  },
  
  error: (...args: any[]) => {
    if (shouldLog('error')) {
      console.error('[ERROR]', ...args)
    }
  },
  
  // AGC: Always log (for critical errors)
  always: (...args: any[]) => {
    console.error('[CRITICAL]', ...args)
  },
  
  // AGC: Group related logs together
  group: (label: string, fn: () => void) => {
    if (isDevelopment) {
      console.group(label)
      fn()
      console.groupEnd()
    } else {
      fn()
    }
  },
}

// AGC: Export default logger
export default logger


